import { Injectable } from '@angular/core';
import { SubscriptionService } from './subscription.service';
import { PricingTier } from '../types/pricing.types';

export interface PaymentCalculation {
  numberOfKilns: number;
  frequency: 'monthly' | 'annual';
  pricePerKiln: number;
  totalPrice: number;
  annualSavings?: number;
  monthlyEquivalent?: number;
  billingCycles: number;
}

@Injectable({
  providedIn: 'root'
})
export class PaymentCalculatorService {

  private pricingTiers: PricingTier[] = [];

  constructor(private subscriptionService: SubscriptionService) {
    this.subscriptionService.getPricingTiers().then(tiers => {
      this.pricingTiers = tiers;
    });
  }

  /**
   * Calculate payment based on number of kilns and frequency
   * @param numberOfKilns - Number of kilns to calculate pricing for
   * @param frequency - 'monthly' or 'annual'
   * @param billingCycles - Number of billing cycles (defaults to 1)
   * @returns PaymentCalculation object with pricing details
   */
  calculatePayment(
    numberOfKilns: number, 
    frequency: 'monthly' | 'annual', 
    billingCycles: number = 1
  ): PaymentCalculation {
    
    if (numberOfKilns <= 0) {
      throw new Error('Number of kilns must be greater than 0');
    }

    // Find the appropriate pricing tier
    const tier = this.getPricingTier(numberOfKilns);
    
    // Calculate price per kiln based on frequency
    const pricePerKiln = frequency === 'monthly' 
      ? tier.monthlyPricePerKiln 
      : tier.annualPricePerKiln;

    // Calculate total price
    const totalPrice = pricePerKiln * numberOfKilns * billingCycles;

    // Calculate additional information
    const calculation: PaymentCalculation = {
      numberOfKilns,
      frequency,
      pricePerKiln,
      totalPrice,
      billingCycles
    };

    // Add annual savings if monthly frequency
    if (frequency === 'monthly') {
      const annualEquivalent = this.calculatePayment(numberOfKilns, 'annual', 1);
      calculation.annualSavings = (annualEquivalent.totalPrice - (totalPrice * 12));
      calculation.monthlyEquivalent = totalPrice;
    } else {
      // Add monthly equivalent if annual frequency
      calculation.monthlyEquivalent = totalPrice / 12;
    }

    return calculation;
  }

  /**
   * Get pricing tier for a given number of kilns
   * @param numberOfKilns - Number of kilns
   * @returns PricingTier object
   */
  private getPricingTier(numberOfKilns: number): PricingTier {
    const tier = this.pricingTiers.find(t => 
      numberOfKilns >= t.minKilns && numberOfKilns <= t.maxKilns
    );
    
    if (!tier) {
      throw new Error(`No pricing tier found for ${numberOfKilns} kilns`);
    }
    
    return tier;
  }

  /**
   * Get all available pricing tiers
   * @returns Array of PricingTier objects
   */
  getPricingTiers(): PricingTier[] {
    return [...this.pricingTiers];
  }

  /**
   * Calculate pricing for multiple scenarios
   * @param numberOfKilns - Number of kilns
   * @returns Object with both monthly and annual calculations
   */
  calculateBothFrequencies(numberOfKilns: number): {
    monthly: PaymentCalculation;
    annual: PaymentCalculation;
  } {
    return {
      monthly: this.calculatePayment(numberOfKilns, 'monthly'),
      annual: this.calculatePayment(numberOfKilns, 'annual')
    };
  }

  /**
   * Format price as currency string
   * @param price - Price to format
   * @param currency - Currency code (default: 'USD')
   * @returns Formatted price string
   */
  formatPrice(price: number, currency: string = 'USD'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(price);
  }

  /**
   * Get plan ID for subscription service
   * @param frequency - 'monthly' or 'annual'
   * @returns Plan ID string
   */
  getPlanId(frequency: 'monthly' | 'annual'): string {
    return frequency === 'monthly' ? 'kiln_monthly' : 'kiln_annual';
  }

  /**
   * Update pricing tiers (useful for admin configuration)
   * @param newTiers - New pricing tiers
   */
  updatePricingTiers(newTiers: PricingTier[]): void {
    this.pricingTiers.splice(0, this.pricingTiers.length, ...newTiers);
  }
} 